import * as React from "react";
import { UseQueryResult } from "react-query";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "react-router-dom";
import bgBrush from "assets/images/hug-brush-6.svg";
import defaultProjectSrc from "assets/images/default-project-img.svg";
import placeholderAvatar from "assets/images/default-avatar.svg";
import {
  AllCaps,
  BrushWrapper,
  Button,
  Container,
  ExpandContent,
  FlexRow,
  H1,
  H2,
  H3,
  Hr,
  IconChat,
  IconEnvelopeHeart,
  IconMapMarker,
  IconUser,
  Link,
  List,
  Paragraphs,
  SiteContainer,
  Stack,
  Tag,
  TagLink,
  Text,
  Tooltip,
  YouTubeEmbed,
} from "atoms";
import { trackEvent } from "contexts/mixpanel";
import { useAuth } from "features/auth";
import { useCollectives } from "features/collectives";
import { FollowButton, FollowInviteBanner } from "features/follows";
import { FeaturedImage, ReviewableBanner, Thumbnail } from "features/images";
import { Head } from "features/misc";
import { useOpenCall, useOpenCallSubmissions } from "features/openCalls";
import { ProfileFeed } from "features/posts";
import {
  AffiliatedCollections,
  ArtistAdminSection,
  ArtistCollections,
  BetaProfileConvertMessage,
  IconWrapper,
  InviteMenu,
  LinkList,
  ProjectSpecs,
  ProjectTeam,
  RequestCommission,
  ReviewableTemplatePlaceholder,
  RoadmapCategories,
  SecondaryMenu,
  ShareMenu,
  SocialLink,
  TopReactions,
  type Artist,
  type Project,
} from "features/reviewable";
import {
  ReviewList,
  ArtistReviewStats,
  ProjectReviewStats,
  UsersReview,
  useReviews,
  useReviewSort,
  useUsersReview,
} from "features/reviews";
import { CreateTicket } from "features/tickets";
import { ApiError } from "utils/api";
import { formatDate } from "utils/date";
import { formatLocation } from "utils/location";

const trackSignIn = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign In",
    ButtonContext: "Reviewable Profile",
  });
const trackSignUp = () =>
  trackEvent({
    name: "Button Click",
    ButtonName: "Sign Up",
    ButtonContext: "Reviewable Profile",
  });

type ReviewableTemplateProps<TEntity extends Artist | Project> =
  UseQueryResult<TEntity> & {
    notFound: React.ReactNode;
  };

function ReviewableTemplate<TEntity extends Artist | Project>({
  data: entity,
  error,
  isLoading,
  notFound,
}: ReviewableTemplateProps<TEntity>) {
  const { search } = useLocation();

  const { activeReviewSort, createReviewSortHandler } = useReviewSort();

  const navigate = useNavigate();

  const {
    user,
    roles: { showAdminFeatures, isPoster },
  } = useAuth();

  const { data: usersReview } = useUsersReview({
    entityId: entity?.id,
    entityType: entity?.type,
  });

  const allReviewPages = useReviews({
    entityId: entity?.id,
    entityType: entity?.type,
    perPage: 1,
  });
  const totalReviewCount = allReviewPages.data?.pages?.[0]?.totalResults ?? 0;

  const [isMoreOpen, setIsMoreOpen] = React.useState<boolean>(false);
  const toggleMore = () => {
    setIsShareOpen(false);
    setIsInviteOpen(false);
    setIsMoreOpen(!isMoreOpen);
  };

  const [isShareOpen, setIsShareOpen] = React.useState<boolean>(false);
  const toggleShare = React.useCallback(() => {
    setIsMoreOpen(false);
    setIsInviteOpen(false);
    setIsShareOpen((old) => !old);
  }, [setIsShareOpen]);

  const [isInviteOpen, setIsInviteOpen] = React.useState<boolean>(false);
  const toggleInvite = () => {
    setIsMoreOpen(false);
    setIsShareOpen(false);
    setIsInviteOpen(!isInviteOpen);
  };

  const [isReportFormOpen, setIsReportFormOpen] =
    React.useState<boolean>(false);
  const openReportForm = () => {
    setIsMoreOpen(false);
    setIsReportFormOpen(true);
  };
  const closeReportForm = () => {
    setIsReportFormOpen(false);
  };

  const [isCommissionFormOpen, setIsCommissionFormOpen] =
    React.useState<boolean>(false);
  const openCommissionForm = () => {
    setIsShareOpen(false);
    setIsMoreOpen(false);
    setIsCommissionFormOpen(true);
  };
  const closeCommissionForm = () => setIsCommissionFormOpen(false);

  if (isLoading) {
    return <ReviewableTemplatePlaceholder isLoading />;
  }

  if (!entity) {
    return (
      <SiteContainer width="md">
        {error instanceof ApiError && error?.status === 404
          ? notFound
          : "Sorry, we had a problem loading that resource."}
      </SiteContainer>
    );
  }

  const entityType = entity.type.toLowerCase();

  // Content checks for common to all Reviewables
  const hasHighlights = !!entity.highlights?.length;
  const hasRoadmapCategories = !!entity.roadmapCategories?.length;
  const reviewCountText = `${totalReviewCount} Review${
    totalReviewCount === 1 ? "" : "s"
  }`;
  const followerCountText = `${entity.followerCount ?? 0} Follower${
    (entity.followerCount ?? 0) === 1 ? "" : "s"
  }`;

  // Overrides for each specific Reviewable type based on current entity
  const isArtist = entity.type === "ARTIST";
  const isProject = entity.type === "PROJECT";

  const hasTags = !!(
    entity.founderAttributes?.length ||
    entity.missionOrCause?.length ||
    (isArtist && entity.medium?.length)
  );
  const hasArtistCollection = isArtist && !!entity.artistCollections.length;
  const hasProjectTeam = isProject && !!entity.team.length;
  const hasAffiliated = !!entity.affiliations.length;
  const showCommissions = isArtist && entity?.acceptsCommissions;
  const isArtistAndAdmin = isArtist && showAdminFeatures;
  const userIsCurrentArtist = user?.claimedArtistPageIds?.includes(entity.id);
  const isFollowInvite = search.endsWith("follow");

  const pronouns =
    isArtist && entity.pronouns && entity.pronouns.length
      ? entity.pronouns
      : undefined;

  // User is an admin for this artist
  const isProfileAdmin = !!user?.claimedArtistPageIds?.includes(entity.id);

  const location = isArtist ? formatLocation(entity) : undefined;

  return (
    <>
      {isArtist ? (
        <Head
          title={`${entity.name} | Artist | HUG`}
          description={`Learn more about artist ${entity.name}, discover and click through to shop their artwork, and connect with them by leaving reviews.`}
          socialTitle={`${entity.name} | HUG`}
          socialDescription={`Discover art from ${entity.name} and connect further by leaving a thoughtful and meaningful review.`}
          socialImage={
            entity.artistCollections?.[0]?.collectionImage?.url ??
            entity.heroImage?.url ??
            placeholderAvatar
          }
          socialImageAltText={`Artwork by ${entity.name}`}
          type="profile"
          username={entity.name}
        />
      ) : (
        <Head
          title={`${entity.name} | NFT Project | HUG`}
          description={`Learn more about artist ${entity.name}, discover and click through to shop their artwork, and connect with them by leaving reviews.`}
          socialTitle={`${entity.name} | HUG`}
          socialDescription={`Discover art from ${entity.name} and connect further by leaving a thoughtful and meaningful review.`}
          socialImage={entity?.heroImage?.url ?? placeholderAvatar}
          socialImageAltText={`Collection image from ${entity.name}.`}
          type="profile"
          username={entity.name}
        />
      )}

      {isArtist && isFollowInvite && (
        <FollowInviteBanner
          artistId={entity.id}
          artistName={entity.name}
          user={user}
        />
      )}

      {isArtist &&
        (isProfileAdmin || showAdminFeatures) &&
        entity?.betaProfile === "INVITED" && (
          <BetaProfileConvertMessage artistId={entity.id} />
        )}

      <ReviewableBannerImage entityType={entityType}>
        <ReviewableBanner
          src={entity.bannerImage}
          fallback={entity?.heroImage ?? placeholderAvatar}
          alt=""
          aria-hidden="true"
        />
      </ReviewableBannerImage>

      <ReviewableContainer>
        <ReviewableGrid className={`entity_${entityType}`}>
          <ProfileHeader>
            <ReviewableFeaturedImage
              src={entity?.heroImage ?? placeholderAvatar}
              alt={`Featured NFT from ${entity.name} collection`}
              entityType={entity.type}
            />

            <ReviewableTitle>
              <EntityName lang="en">{entity.name ?? "Artist Name"}</EntityName>
              {entity.status === "DRAFT" && <DraftTag>Draft</DraftTag>}
              {pronouns && (
                <Text size="xs" as="span">
                  ({pronouns})
                </Text>
              )}
            </ReviewableTitle>

            <ReviewableActions>
              {isArtist && (
                <>
                  {!userIsCurrentArtist && (
                    <FollowButton size="xxxs" entityId={entity.id} />
                  )}

                  {userIsCurrentArtist && isPoster && (
                    <Link
                      to={`/artists/${entity.id}/create-update`}
                      variant="primary"
                      size="xxxs"
                    >
                      Post an Update
                    </Link>
                  )}

                  {(userIsCurrentArtist || showAdminFeatures) && (
                    <InviteMenu
                      reviewable={entity}
                      isAdmin={showAdminFeatures}
                      isArtist={userIsCurrentArtist}
                      isOpen={isInviteOpen}
                      toggle={toggleInvite}
                    />
                  )}
                </>
              )}

              {isProject &&
                (user && !usersReview ? (
                  <Link
                    variant="primary"
                    size="xxxs"
                    to={`/${entityType}s/${entity.id}/review`}
                  >
                    Write a review
                  </Link>
                ) : (
                  <TopReviewButtonTooltip
                    label={
                      <Button variant="primary" size="xxxs" as="div">
                        Write a review
                      </Button>
                    }
                  >
                    <ReviewSigninContent />
                  </TopReviewButtonTooltip>
                ))}

              <ShareMenu
                reviewable={entity}
                isShareMenuOpen={isShareOpen}
                toggleShareMenu={toggleShare}
              />

              <SecondaryMenu
                toggleSecondaryMenu={toggleMore}
                isSecondaryMenuOpen={isMoreOpen}
                openReportForm={openReportForm}
                reviewable={entity}
              />
            </ReviewableActions>

            <Counts>
              <FlexRow>
                <CountsLink
                  to={`/${entityType}s/${entity.id}#reviews`}
                  aria-label={`${reviewCountText}. Jump to all reviews.`}
                >
                  {totalReviewCount === 0 ? (
                    <IconWrapper>
                      <IconChat style={{ margin: 0, width: "55%" }} />
                    </IconWrapper>
                  ) : (
                    <TopReactions entity={entity} aria-hidden />
                  )}
                  <Text size="xs" as="span">
                    {reviewCountText}
                  </Text>
                </CountsLink>

                {isArtist && (
                  <FlexRow gap="10px">
                    <IconWrapper>
                      <IconUser
                        primary="fg"
                        style={{ margin: 0, width: "55%" }}
                      />
                    </IconWrapper>{" "}
                    <Text size="xs" as="span">
                      <Link to={`/artists/${entity.id}/followers`}>
                        {followerCountText}
                      </Link>
                    </Text>
                  </FlexRow>
                )}

                {location && (
                  <FlexRow gap="10px">
                    <IconWrapper>
                      <IconMapMarker
                        primary="fg"
                        style={{ margin: 0, width: "55%" }}
                      />
                    </IconWrapper>
                    <Text size="xs" as="span">
                      {location}
                    </Text>
                  </FlexRow>
                )}
              </FlexRow>
            </Counts>
          </ProfileHeader>
          <ProfileMain>
            {hasTags && (
              <TagList>
                {isArtist &&
                  (entity?.medium || []).map((tag) => (
                    <TagLink
                      tag={tag}
                      variant="medium"
                      key={tag}
                      analyticsLocation="Profile"
                    />
                  ))}
                {(entity?.founderAttributes || []).map((tag) => (
                  <TagLink
                    tag={tag}
                    variant="founderAttributes"
                    key={tag}
                    analyticsLocation="Profile"
                  />
                ))}
                {(entity?.missionOrCause || []).map((tag) => (
                  <TagLink
                    tag={tag}
                    variant="missionOrCause"
                    key={tag}
                    analyticsLocation="Profile"
                  />
                ))}
              </TagList>
            )}

            {showCommissions && (
              <Announcements>
                <Announcement isOpen={isCommissionFormOpen}>
                  <AnnouncementMain>
                    <AnnouncementIcon>
                      <IconEnvelopeHeart primary="bg" accent="accent4" />
                    </AnnouncementIcon>
                    <AnnouncementBody>
                      <div>
                        <H2 size="md" textTransform="uppercase">
                          Commission Custom Artwork
                        </H2>
                        <Text size="xxs">
                          {entity.name} is open to commissions.
                          Curious&nbsp;about a&nbsp;custom piece?
                          Send&nbsp;a&nbsp;request to&nbsp;start the
                          conversation&nbsp;today.
                        </Text>
                      </div>

                      <Button
                        onClick={() => {
                          openCommissionForm();
                          navigate("#request-commission");
                        }}
                        size="xs"
                        arrow="r"
                        variant="secondary"
                        disabled={isCommissionFormOpen}
                      >
                        Send Request
                      </Button>
                    </AnnouncementBody>
                  </AnnouncementMain>

                  {isCommissionFormOpen && (
                    <div id="request-commission">
                      <Hr />
                      <RequestCommission
                        artist={entity}
                        isOpen={isCommissionFormOpen}
                        close={closeCommissionForm}
                      />
                    </div>
                  )}
                </Announcement>
              </Announcements>
            )}

            <ProfileForms>
              {isReportFormOpen && (
                <InlineFormWrapper id="report-form">
                  <CreateTicket
                    reviewableId={entity.id}
                    reviewableType={entity.type}
                    isOpen={isReportFormOpen}
                    close={closeReportForm}
                  />
                </InlineFormWrapper>
              )}
            </ProfileForms>

            {isProject && <ProjectSpecs project={entity} />}

            <Description>
              <ExpandContent
                showLabel="Expand description"
                hideLabel="Collapse description"
              >
                <Paragraphs text={entity.description} size="xs" />

                {hasHighlights && (
                  <Highlights>
                    <H2 size="md" textTransform="uppercase">
                      Highlights
                    </H2>
                    <HighlightsList>
                      {entity.highlights.map((h: string) => (
                        <Text size="xs" as="li" key={h}>
                          {h}
                        </Text>
                      ))}
                    </HighlightsList>
                  </Highlights>
                )}
              </ExpandContent>
            </Description>

            {entity?.youTubeVideo && (
              <Video className="YouTubeEmbed">
                <YouTubeEmbed embedId={entity?.youTubeVideo} />
              </Video>
            )}

            {isArtist && <ProfileFeed artistId={entity.id} />}

            {isArtist && hasArtistCollection && (
              <RelatedCollections>
                <H2 size="lg" textTransform="uppercase">
                  Portfolio
                </H2>
                <ArtistCollections collections={entity.artistCollections} />
              </RelatedCollections>
            )}

            {hasRoadmapCategories && (
              <Roadmap>
                <H2 size="md" textTransform="uppercase">
                  Roadmap Categories
                </H2>
                <RoadmapCategories categories={entity.roadmapCategories} />
              </Roadmap>
            )}

            {hasProjectTeam && (
              <Team>
                <H2 size="md" textTransform="uppercase">
                  Team
                </H2>
                <ProjectTeam team={entity.team} />
              </Team>
            )}

            {hasAffiliated && (
              <Affiliations>
                <H2 size="md" textTransform="uppercase">
                  Affiliations
                </H2>
                <AffiliatedCollections affiliations={entity.affiliations} />
              </Affiliations>
            )}
          </ProfileMain>

          <ReviewableSidebar>
            <ReviewableLinks entity={entity} />
            {isArtist && <ArtistBadges artistId={entity?.id} />}

            {isArtistAndAdmin && <ArtistAdminSection entity={entity} />}
          </ReviewableSidebar>
        </ReviewableGrid>
      </ReviewableContainer>

      <ReviewsWrapper>
        <ReviewBG bg="fg05" brush="1" aria-hidden="true" />
        <ReviewableContainer>
          <ReviewsHeader>
            <H2 id="reviews" textTransform="uppercase">
              Reviews
            </H2>

            <WriteReviewLink
              userIsSignedIn={!!user}
              userIsCurrentArtist={!!userIsCurrentArtist}
              userHasReviewed={!!usersReview}
              entityType={entityType}
              entityId={entity.id}
            />
          </ReviewsHeader>

          {totalReviewCount ? (
            <ReviewColumns hasReviews>
              <div>
                <UsersReview review={usersReview} />

                <StatsBlock>
                  {entity.type === "ARTIST" && (
                    <ArtistReviewStats artistId={entity.id} />
                  )}
                  {entity.type === "PROJECT" && (
                    <ProjectReviewStats projectId={entity.id} />
                  )}
                </StatsBlock>
              </div>

              <ReviewsColumn>
                <ReviewList
                  entityType={entity.type}
                  entityId={entity.id}
                  reviewCount={totalReviewCount}
                  activeReviewSort={activeReviewSort}
                  createReviewSortHandler={createReviewSortHandler}
                />
              </ReviewsColumn>
            </ReviewColumns>
          ) : (
            <NoReviews gap="sm">
              <H3 textAlign="center">
                {entity.name} doesn&apos;t have a review yet!
              </H3>
              <Text size="xs" textAlign="center">
                There are no reviews posted. If you are one of the first 5
                reviews you get double the <AllCaps>Hug</AllCaps>&nbsp;Points!
              </Text>
            </NoReviews>
          )}
        </ReviewableContainer>
      </ReviewsWrapper>
    </>
  );
}

function ReviewSigninContent() {
  // Successful sign-in will redirect, but we want to end up at the review form and not back here
  const { pathname, ...location } = useLocation();
  const from = {
    ...location,
    pathname: pathname.endsWith("/review") ? pathname : `${pathname}/review`,
  };

  return (
    <Stack gap="sm">
      <SText bold size="sm" font="display">
        Create an account or sign&nbsp;in to&nbsp;write&nbsp;a&nbsp;review.
      </SText>
      <FlexRow gap="10px" flexWrap="nowrap">
        <Link
          to="/sign-in"
          state={{ from }}
          variant="secondary"
          size="xs"
          onClick={trackSignIn}
        >
          Sign In
        </Link>
        <Link
          to="/register"
          state={{ from }}
          variant="secondary"
          size="xs"
          onClick={trackSignUp}
        >
          Create Account
        </Link>
      </FlexRow>
    </Stack>
  );
}

type WriteReviewLinkProps = {
  userIsSignedIn: boolean;
  userIsCurrentArtist: boolean;
  userHasReviewed: boolean;
  entityType: string;
  entityId: string;
};
function WriteReviewLink({
  userIsSignedIn,
  userIsCurrentArtist,
  userHasReviewed,
  entityId,
  entityType,
}: WriteReviewLinkProps) {
  if (userIsSignedIn) {
    // User has not already reviewed, and is not on their own profile page
    if (!userIsCurrentArtist && !userHasReviewed) {
      return (
        <Link
          variant="secondary"
          size="sm"
          to={`/${entityType}s/${entityId}/review`}
        >
          Write a review
        </Link>
      );
    }
  } else {
    // User is not signed in
    return (
      <Tooltip
        positionY="bottom"
        label={
          <Button variant="primary" size="sm" as="div">
            Write a review
          </Button>
        }
      >
        <ReviewSigninContent />
      </Tooltip>
    );
  }
  return null;
}

type ReviewableLinksProps = { entity: Artist | Project };
function ReviewableLinks({ entity }: ReviewableLinksProps) {
  const isArtist = entity.type === "ARTIST";
  return (
    <Links>
      <LinksH2 size="sm" textTransform="uppercase">
        Links
      </LinksH2>
      <LinkList>
        {isArtist && (
          <>
            {entity.webSiteURL && (
              <li>
                <SocialLink
                  name={entity.name}
                  link={{ url: entity.webSiteURL, type: "website" }}
                />
              </li>
            )}
            {entity.instagramHandle && (
              <li>
                <SocialLink
                  name={entity.name}
                  link={{
                    url: `https://instagram.com/${entity.instagramHandle}`,
                    type: "instagram",
                  }}
                />
              </li>
            )}
            {entity.twitterHandle && (
              <li>
                <SocialLink
                  name={entity.name}
                  link={{
                    url: `https://x.com/${entity.twitterHandle}`,
                    type: "twitter",
                  }}
                />
              </li>
            )}
            {entity.youTubeHandle && (
              <li>
                <SocialLink
                  name={entity.name}
                  link={{
                    url: `https://youtube.com/${entity.youTubeHandle}`,
                    type: "youTubeURL",
                  }}
                />
              </li>
            )}
            {entity.tikTokHandle && (
              <li>
                <SocialLink
                  name={entity.name}
                  link={{
                    url: `https://tiktok.com/${entity.tikTokHandle}`,
                    type: "tiktok",
                  }}
                />
              </li>
            )}
          </>
        )}
        {entity?.links?.map((link) => (
          <li key={link.url}>
            <SocialLink name={entity.name} link={link} />
          </li>
        ))}
      </LinkList>
    </Links>
  );
}

const ReviewableContainer = styled(Container)({});
ReviewableContainer.defaultProps = {
  width: "lg",
};

const ReviewsHeader = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: "2rem",
  position: "relative",
  zIndex: 10,
  gap: "1rem",
  [theme.breakpoints.tablet]: {
    flexDirection: "row",
    alignItems: "center",
  },
  h2: {
    margin: 0,
  },
}));

const ReviewableBannerImage = styled.figure<{
  entityType: string;
}>(({ entityType, theme }) => ({
  aspectRatio: entityType === "artist" ? "4" : "none",
  backgroundColor: theme.colors.fg10,
  height: entityType === "artist" ? "auto" : "20vw",
  maxHeight: entityType === "artist" ? 320 : 160,
  minHeight: 60,
  minWidth: "100%",
  overflow: "hidden",
  width: "100%",
}));

const ReviewableGrid = styled.div(({ theme }) => ({
  position: "relative",
  [theme.breakpoints.desktop]: {
    display: "grid",
    gap: "0 2rem",
    gridTemplateColumns: "1fr auto",
    gridTemplateAreas: `
        "header sidebar"
        "main sidebar"
      `,
  },
  [theme.breakpoints.desktopLarge]: {
    gridTemplateColumns: "1fr 275px",
  },
}));

const ProfileHeader = styled.div(({ theme }) => ({
  gridArea: "header",
  display: "grid",
  gap: "0",
  position: "relative",
  gridTemplateColumns: "auto 1fr",
  gridTemplateAreas: `
    "image actions"
    "title title"
    "counts counts"
  `,
  [theme.breakpoints.desktop]: {
    gap: "0 2rem",
  },
}));

const ReviewableFeaturedImage = styled(FeaturedImage)(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  border: `solid .25em ${theme.colors.bg}`,
  boxShadow: theme.boxShadow.light,
  gridArea: "image",
  overflow: "hidden",
  margin: 0,
  height: 90,
  marginTop: "calc(90px / -2)",
  width: 90,
  [theme.breakpoints.xs]: {
    height: 110,
    marginTop: "calc(110px / -2)",
    width: 110,
  },
  [theme.breakpoints.tablet]: {
    height: 150,
    marginTop: "calc(150px / -2)",
    width: 150,
  },
  [theme.breakpoints.desktop]: {
    height: 180,
    marginTop: "calc(180px / -2)",
    width: 180,
  },
}));

const ReviewableTitle = styled.header({
  gridArea: "title",
  alignSelf: "center",
  display: "inline-flex",
  flexWrap: "wrap",
  gap: 10,
  alignItems: "center",
  margin: "1rem 0",
});

const DraftTag = styled(Tag)(({ theme }) => ({
  backgroundColor: theme.colors.accent3LL,
  border: `1px solid ${theme.colors.accent3}`,
  fontWeight: "bold",
  fontSize: theme.fontSizes.xxs,
}));

const EntityName = styled(H1)(({ theme }) => ({
  fontSize: theme.fontSizes.h2,
  display: "inline-block",
  hyphens: "auto",
  lineHeight: "1",
  marginBottom: 0,
  wordBreak: "break-word",
}));

const Counts = styled.div({
  gridArea: "counts",
  marginBottom: "1rem",
});

const CountsLink = styled(Link)({
  alignItems: "center",
  display: "inline-flex",
  span: {
    margin: 0,
  },
  "*+span": {
    marginLeft: 10,
  },
});

const ReviewableActions = styled(FlexRow)(({ theme }) => ({
  margin: "1rem 0 0",
  gridArea: "actions",
  gap: 10,
  justifyContent: "flex-end",
  alignItems: "flex-start",
  [theme.breakpoints.maxTablet]: {
    position: "static",
    marginLeft: "-1rem",
  },
  [theme.breakpoints.desktop]: {
    margin: "2rem 0 1rem -1.5rem",
    position: "relative",
  },
  ".Tooltip": {
    [theme.breakpoints.maxTablet]: {
      position: "static",
    },
  },
  ".TooltipContent": {
    [theme.breakpoints.maxTablet]: {
      top: -60,
      right: 0,
      bottom: "auto",
      left: "auto",
      maxWidth: "100%",
      "&::before, &::after": {
        content: "none",
      },
    },
    [theme.breakpoints.tablet]: {
      left: "50%",
      maxWidth: 270,
      transform: "translate(-50%, 0)",
      "&::before, &::after": {
        left: "50%",
        transform: "translateX(-50%)",
        margin: 0,
      },
    },
  },
}));

const TagList = styled.div(({ theme }) => ({
  gridArea: "tags",
  flex: "0 0 100%",
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  marginBottom: "1.5rem",
  a: {
    fontSize: theme.fontSizes.xxxxs,
  },
}));

const Description = styled.div({
  gridArea: "description",
  marginBottom: "2rem",
});

const ProfileForms = styled.div({
  gridArea: "profile-forms",
});

const InlineFormWrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.fg05,
  borderRadius: theme.borderRadius.md,
  margin: "0 0 2rem 0",
  padding: "2rem 1rem",
  [theme.breakpoints.desktop]: {
    padding: "2rem",
  },
}));

const ProfileMain = styled.div({
  gridArea: "main",
});
const ReviewableSidebar = styled.div(({ theme }) => ({
  gridArea: "sidebar",
  [theme.breakpoints.desktop]: {
    maxWidth: 230,
  },
}));

const Links = styled.div(({ theme }) => ({
  margin: "3rem 0 2rem",
  [theme.breakpoints.desktop]: {
    borderLeft: `1px solid ${theme.colors.fg20}`,
    margin: "2rem 0",
    padding: "0 0 0 2rem",
  },
}));

const LinksH2 = styled(H2)(({ theme }) => ({
  [theme.breakpoints.desktop]: {
    display: "none",
  },
}));

const Video = styled.div({
  gridArea: "video",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "2rem",
});

const Highlights = styled.div({
  marginTop: "1.5rem",
});

const Roadmap = styled.div({
  gridArea: "roadmap",
  marginBottom: "3rem",
  maxWidth: "calc(100vw - 2rem)",
});

const RelatedCollections = styled.div({
  gridArea: "collections",
  marginBottom: "3rem",
});

const ReviewsWrapper = styled.div({
  marginTop: "3rem",
  padding: "3rem 0 6rem",
  position: "relative",
  overflow: "hidden",
});

const ReviewBG = styled(BrushWrapper)({
  position: "absolute",
  top: 0,
  right: 0,
  left: 0,
  bottom: 0,
  zIndex: 0,
  maxHeight: "100%",
  "&::before": {
    aspectRatio: "1",
    backgroundSize: "115vmax",
    backgroundPosition: "top",
    top: 0,
    transform: "translateY(-3%)",
    opacity: 0.025,
  },
});

const NoReviews = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.bg,
  borderRadius: theme.borderRadius.md,
  padding: "2rem 1rem",
  position: "relative",
}));

const ReviewColumns = styled.div<{ hasReviews: boolean }>(
  ({ hasReviews, theme }) => ({
    position: "relative",
    zIndex: 1,
    ...(hasReviews && {
      [theme.breakpoints.desktop]: {
        display: "grid",
        gap: "1.5rem",
        gridTemplateColumns: "7fr 3fr",
        gridTemplateAreas: `"reviews stats"`,
        alignItems: "start",
      },
    }),
  })
);

const ReviewsColumn = styled.div(({ theme }) => ({
  gridArea: "reviews",
  backgroundColor: theme.colors.bg,
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.boxShadow.light,
  padding: "1rem",
  marginBottom: "1rem",
}));

const StatsBlock = styled.div(({ theme }) => ({
  gridArea: "stats",
  backgroundColor: theme.colors.bg,
  borderRadius: theme.borderRadius.md,
  boxShadow: theme.boxShadow.light,
  padding: "1rem",
  marginBottom: "1rem",
}));

const HighlightsList = styled(Text)({
  li: {
    marginBottom: "0.66rem",
  },
}).withComponent("ul");

const Team = styled.div({
  gridArea: "team",
});

const Affiliations = styled.div({
  gridArea: "affiliations",
  marginBottom: "3rem",
  marginTop: "2rem",
});

const Announcements = styled.div({
  gridArea: "announcements",
  "&>*": {
    marginBottom: "2rem",
  },
});

const largeGutter = "calc((100vw - (1140px - 6.4rem)) / 2)";
const Announcement = styled.div<{ isOpen: boolean }>(({ isOpen, theme }) => ({
  backgroundColor: theme.colors.fg,
  borderRadius: isOpen
    ? "0 90px 90px 0"
    : `0 ${theme.borderRadius.round}px ${theme.borderRadius.round}px 0`,
  "&, label, a, h2, h3, legend": { color: theme.colors.bg },
  marginLeft: `calc(${theme.spacing.gutter} * -1)`,
  overflow: "hidden",
  padding: "2rem",
  paddingLeft: theme.spacing.gutter,
  position: "relative",
  [theme.breakpoints.desktopLarge]: {
    marginLeft: `calc(${largeGutter} * -1)`,
    paddingLeft: largeGutter,
  },
  [theme.breakpoints.desktopXLarge]: {
    borderRadius: isOpen ? 90 : theme.borderRadius.round,
    marginLeft: 0,
    padding: "2rem 3rem",
  },
  "&>div": {
    position: "relative",
    zIndex: 2,
  },
  "&::before": {
    content: "''",
    backgroundImage: `url(${bgBrush})`,
    backgroundPosition: "top right",
    backgroundRepeat: "no-repeat",
    backgroundSize: "75%",
    borderRadius: "inherit",
    position: "absolute",
    pointerEvents: "none",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
  "input:checked": {
    borderColor: theme.colors.bg,
    backgroundColor: theme.colors.fg,
  },
}));
const AnnouncementMain = styled.div(({ theme }) => ({
  [theme.breakpoints.xs]: {
    display: "flex",
    gap: 10,
  },
}));

const AnnouncementIcon = styled.div(({ theme }) => ({
  fontSize: 40,
  width: 65,
  height: "auto",
  marginTop: 4,
  display: "none",
  [theme.breakpoints.tablet]: {
    display: "block",
  },
}));

const AnnouncementBody = styled.div(({ theme }) => ({
  lineHeight: 1.3,
  h2: {
    marginBottom: 6,
  },
  [theme.breakpoints.tablet]: {
    display: "flex",
    alignItems: "center",
    gap: "10px 2rem",
    flexWrap: "wrap",
    div: {
      flex: "1 1 300px",
      minWidth: 280,
    },
    button: {
      flex: "0 0 auto",
      justifyContent: "center",
    },
    "p:last-of-type": {
      marginBottom: 0,
    },
  },
}));

const SText = styled(Text)(({ theme }) => ({
  lineHeight: 1.3,
  color: theme.colors.fg,
}));

const TopReviewButtonTooltip = styled(Tooltip)({
  "&>div": {
    maxWidth: "18em",
  },
  "@media(max-width: 400px)": {
    "&>div": {
      position: "fixed",
      top: "auto",
      left: "50%",
      transform: `translate(-50%, 4px)`,
      "&::before, &::after": {
        display: "none",
      },
    },
  },
});
TopReviewButtonTooltip.defaultProps = {
  positionX: "right",
  positionY: "bottom",
};

function ArtistBadges({ artistId }: { artistId: string }) {
  const { data } = useCollectives({
    collectiveArtistIds: [artistId],
    perPage: 99,
  });

  const collectivesAndBadges = data?.pages?.flatMap((page) => page.data) ?? [];
  const collectives = collectivesAndBadges.filter(
    (item) => item.hasCollectivePage && item.badge
  );
  const badges = collectivesAndBadges.filter((item) => !item.hasCollectivePage);

  return (
    <Stack gap="md">
      {!!collectives.length && (
        <Stack gap="sm">
          <H2 size="sm">Collectives</H2>
          <BadgeList type="blank">
            {collectives.map(({ id, name, badge }) => (
              <li key={id}>
                <SLink to={`/collectives/${id}`}>
                  <SThumbnail src={badge} aria-hidden entityType="PROJECT" />
                  <Stack gap="0">
                    <BadgeTitle>{name}</BadgeTitle>
                  </Stack>
                </SLink>
              </li>
            ))}
          </BadgeList>
        </Stack>
      )}

      <GetSelectedOpenCalls artistId={artistId} />

      {!!badges.length && (
        <Stack gap="sm">
          <H2 size="sm">Badges</H2>
          <BadgeList type="blank">
            {badges.map(({ id, name, badge }) => (
              <li key={id}>
                <SLink
                  to={`/badges/${id}`}
                  aria-label={`See all artists with the ${name} badge`}
                >
                  <SThumbnail
                    src={badge}
                    aria-hidden
                    entityType="PROJECT"
                    sizes=""
                  />
                  <Stack gap="0">
                    <BadgeTitle>{name}</BadgeTitle>
                  </Stack>
                </SLink>
              </li>
            ))}
          </BadgeList>
        </Stack>
      )}
    </Stack>
  );
}

function GetSelectedOpenCalls({ artistId }: { artistId: string }) {
  // get artists selected submissions
  const { data } = useOpenCallSubmissions({
    artistId,
    selectedOnly: true,
    includeFullObject: true,
  });

  // flatten pages
  const selectedSubmissions = data?.pages?.flatMap((page) => page.data) ?? [];
  if (selectedSubmissions.length === 0) return null;

  return (
    <Stack gap="sm">
      <H2 size="sm">Open Calls Won</H2>
      <BadgeList type="blank">
        {/*  map though selected submissions */}
        {selectedSubmissions.map(({ openCallId, selectedAt }) => (
          // for each open call, show the open call's info
          <OpenCallListItem
            openCallId={openCallId}
            date={selectedAt}
            key={openCallId}
          />
        ))}
      </BadgeList>
    </Stack>
  );
}

function OpenCallListItem({
  openCallId,
  date,
}: {
  openCallId: string;
  date: string | undefined;
}) {
  const { data: oc } = useOpenCall(openCallId);

  if (!oc) return null;

  const badgeImage =
    oc.badgeImage ?? oc.heroImage ?? oc.partnerLogo ?? defaultProjectSrc;

  return (
    <li>
      <SLink key={oc.id} to={`/open-calls/${oc.id}`}>
        <SThumbnail src={badgeImage} entityType="PROJECT" aria-hidden />
        <Stack gap="0">
          <BadgeTitle>{oc.title}</BadgeTitle>
          {typeof date === "string" && (
            <Text size="xxxxs">
              {formatDate(date, {
                month: "long",
                year: "numeric",
              })}
            </Text>
          )}
        </Stack>
      </SLink>
    </li>
  );
}

const BadgeList = styled(List)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
  gap: "1rem 10px",
  [theme.breakpoints.tablet]: {
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
  },
  [theme.breakpoints.desktop]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
}));

const SLink = styled(Link)({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  flexWrap: "nowrap",
  maxWidth: 200,
  lineHeight: 1.1,
  textDecoration: "none",
  textAlign: "center",
  small: {
    fontSize: 10,
    opacity: 0.66,
  },
});

const SThumbnail = styled(Thumbnail)({
  objectFit: "contain",
  objectPosition: "center",
});

const BadgeTitle = styled(Text)(({ theme }) => ({
  fontWeight: "bold",
  fontSize: 13,
  "&+p": {
    color: theme.colors.fg60,
  },
}));

export { ReviewableTemplate };
